import { featureCollection } from '@turf/turf';

import {
  getDistanceToFieldMeters,
  HARVEST_PROOF_MAX_DISTANCE_METERS,
  isCloseEnoughToField,
} from './field-proximity';

import { HarvestProofAnswerPublic, isHarvestProofAnswerSubmitted } from '@harvest-proof/data-access';
import { FieldFeature, FieldsFeatureCollection } from '@fields/data-access';


const FIELD_COLOR_FINISHED = '#40c057';
const FIELD_COLOR_PENDING = '#fab005';
const FIELD_COLOR_SELECTED = '#228be6';

export interface AnswerWithDistance {
  answer: HarvestProofAnswerPublic;
  distanceMeters: number | null;
  isInside: boolean;
}

export function buildFieldsFeatureCollection(
  answers: HarvestProofAnswerPublic[],
  options: {
    selectedAnswerId?: number | null;
    insideAnswerId?: number | null;
  } = {},
): FieldsFeatureCollection {
  const { selectedAnswerId, insideAnswerId } = options;

  const features = answers
    .filter((answer) => answer.field?.geometry)
    .map((answer) => {
      const isFinished = isHarvestProofAnswerSubmitted(answer);
      const isSelected = answer.id === selectedAnswerId;
      const isInside = answer.id === insideAnswerId;

      let color = isFinished ? FIELD_COLOR_FINISHED : FIELD_COLOR_PENDING;
      if (isSelected) {
        color = FIELD_COLOR_SELECTED;
      } else if (isInside && !isFinished) {
        color = '#12b886';
      }

      return {
        ...answer.field,
        properties: {
          ...answer.field.properties,
          color,
          answerId: answer.id,
        },
      } as FieldFeature;
    });

  return featureCollection(
    features as GeoJSON.Feature[],
  ) as FieldsFeatureCollection;
}

export function rankAnswersByDistance(
  answers: HarvestProofAnswerPublic[],
  latitude: number | null | undefined,
  longitude: number | null | undefined,
): AnswerWithDistance[] {
  return answers
    .map((answer) => {
      const distanceMeters =
        latitude != null && longitude != null
          ? getDistanceToFieldMeters(answer.field, latitude, longitude)
          : null;

      return {
        answer,
        distanceMeters,
        isInside: isCloseEnoughToField(answer.field, latitude, longitude),
      };
    })
    .sort((a, b) => {
      if (isHarvestProofAnswerSubmitted(a.answer) && !isHarvestProofAnswerSubmitted(b.answer)) {
        return 1;
      }
      if (isHarvestProofAnswerSubmitted(b.answer) && !isHarvestProofAnswerSubmitted(a.answer)) {
        return -1;
      }
      if (a.distanceMeters === null) return 1;
      if (b.distanceMeters === null) return -1;
      return a.distanceMeters - b.distanceMeters;
    });
}

export function findInsidePendingAnswer(
  ranked: AnswerWithDistance[],
): HarvestProofAnswerPublic | null {
  const inside = ranked.filter(
    (item) =>
      !isHarvestProofAnswerSubmitted(item.answer) &&
      item.distanceMeters !== null &&
      item.distanceMeters <= HARVEST_PROOF_MAX_DISTANCE_METERS,
  );

  return inside.length === 1 ? inside[0].answer : null;
}

export function formatDistanceMeters(meters: number | null): string {
  if (meters === null) {
    return '—';
  }
  if (meters <= HARVEST_PROOF_MAX_DISTANCE_METERS) {
    return 'Sur parcelle';
  }
  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }
  return `${(meters / 1000).toFixed(1)} km`;
}
